import { useEffect, useRef, useState } from "react";
import { Avatar, Badge } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import BaseDropdown from "../../../components/ui/BaseDropdown";
import { useInbox } from "../../../hooks/useInbox";
import { decodeHtmlEntities } from "../../../utils/helpers";

const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

/**
 * Inbox dropdown behind the top-bar message icon.
 *
 * Replaces the old notification-derived list: instead of pulling every MESSAGE
 * notification and grouping them by conversationId in the browser, it reads one row
 * per conversation from the inbox API, already ordered by latest activity and
 * carrying the newest message whoever sent it ("You: …" when it is ours).
 */
const Inbox = ({ isInboxDropdownOpen, setIsInboxDropdownOpen }) => {
  const navigate = useNavigate();
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);

  const {
    conversations,
    nextCursor,
    unreadConversationCount,
    isInboxApiLoading,
    isMarkingAllAsRead,
    loadMore,
    refetchInbox,
    markConversationAsReadMutation,
    markAllAsRead,
  } = useInbox({ enabled: isInboxDropdownOpen });

  // Pick up anything sent from another device/tab while the dropdown was shut.
  useEffect(() => {
    if (isInboxDropdownOpen) refetchInbox();
  }, [isInboxDropdownOpen, refetchInbox]);

  const fetchMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      await loadMore();
    } catch (err) {
      console.error("Error loading more conversations:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) fetchMore();
  };

  const openConversation = async (conversation) => {
    setIsInboxDropdownOpen(false);
    if (conversation?.unreadCount > 0) {
      await markConversationAsReadMutation(conversation.conversationId).catch((err) =>
        console.error("Error marking conversation as read:", err),
      );
    }
    // Opened by ROOM unless the room names an engineer of its own. The job
    // discussion and the customer↔team room both have `engineerId: null`, and
    // falling back to the work order's tech there opened the ENGINEER's thread
    // instead of the one that was clicked.
    const params = conversation?.engineerId
      ? `?engineerId=${conversation.engineerId}`
      : `?chatRoomId=${conversation?.conversationId}`;
    navigate(`/workOrder/messages/${conversation?.workOrder?.id}${params}`);
  };

  return (
    <BaseDropdown
      setOpen={setIsInboxDropdownOpen}
      open={isInboxDropdownOpen}
      className="w-[526px] top-[40px] shadow-none !-left-[420px] !px-0 !py-0 "
      style={{
        boxShadow: "0 1px 10px rgba(0, 0, 0, 0.2)",
      }}
    >
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="relative rounded-lg overflow-y-auto"
        style={{
          maxHeight: "500px",
        }}
      >
        <div className="flex items-center justify-between border-b p-3 top-0 sticky bg-white z-10 ">
          <p className="text-base text-dark font-normal">Messages</p>
          {unreadConversationCount > 0 && (
            <p
              className={`text-primary ${isMarkingAllAsRead ? "opacity-50" : "cursor-pointer"}`}
              onClick={() => {
                if (isMarkingAllAsRead) return;
                markAllAsRead().catch((err) =>
                  console.error("Error marking all conversations as read:", err),
                );
              }}
            >
              Mark all as read
            </p>
          )}
        </div>

        {isInboxApiLoading && conversations.length === 0 && (
          <p className="text-xs text-[#A5A5A5] px-4 py-5">Loading conversations…</p>
        )}

        {!isInboxApiLoading && conversations.length === 0 && (
          <p className="text-xs text-[#A5A5A5] px-4 py-5">No messages yet.</p>
        )}

        {conversations.map((conversation) => (
          <Conversation
            key={conversation.conversationId}
            name={conversation?.participant?.name}
            avatar={conversation?.participant?.avatar}
            isWorkOrderChat={!!conversation?.isWorkOrderChat}
            workOrderReference={conversation?.workOrder?.referenceId}
            preview={conversation?.latestMessage?.preview}
            createdAt={
              conversation?.latestMessageAt ? dayjs(conversation.latestMessageAt).fromNow() : ""
            }
            unreadCount={conversation?.unreadCount}
            onClick={() => openConversation(conversation)}
          />
        ))}

        {loadingMore && <p className="text-xs text-[#A5A5A5] px-4 py-3 text-center">Loading…</p>}
      </div>
    </BaseDropdown>
  );
};

export default Inbox;

const Conversation = ({
  name,
  avatar,
  isWorkOrderChat,
  workOrderReference,
  preview,
  createdAt,
  unreadCount,
  onClick,
}) => {
  return (
    <div
      className={`flex px-4 py-5 border-b cursor-pointer ${unreadCount > 0 && "bg-light"}`}
      onClick={onClick}
    >
      {/* Named after the job, not after a party to it — the same icon the chat
          sidebar gives it, rather than a company's logo. */}
      {isWorkOrderChat ? (
        <Avatar>
          <i className="fa-light fa-clipboard-list text-base !text-white" />
        </Avatar>
      ) : (
        <Avatar src={avatar}></Avatar>
      )}
      <div className="ml-3 w-full min-w-0">
        <div className="flex w-full justify-between">
          <div className="flex items-center min-w-0">
            <p className="font-normal text-dark capitalize truncate">{name}</p>
            {workOrderReference && <p className="text-xs ml-2 shrink-0">WO-{workOrderReference}</p>}
          </div>
          <p className="text-[10px] text-[#A5A5A5] shrink-0 ml-2">{createdAt}</p>
        </div>
        <div className="flex justify-between items-center w-full">
          <p
            className={`text-xs min-w-0 truncate ${unreadCount > 0 ? "text-dark" : "text-[#6B7280]"}`}
          >
            {decodeHtmlEntities(preview)}
          </p>
          <Badge
            className="cursor-pointer mr-2"
            color="secondary"
            size="small"
            badgeContent={unreadCount}
            sx={{
              ".MuiBadge-badge": {
                minWidth: "10px",
                height: "18px",
                fontSize: "12px",
              },
            }}
          ></Badge>
        </div>
      </div>
    </div>
  );
};
